import type * as maplibregl from "maplibre-gl";
import type { RailingStyle, ShapeSurface, SurfaceBorder, SurfaceMaterial, SurfaceScale } from "../types";
import { isSurfaceMaterial, isSurfaceScale } from "../../core/surface";

const SOURCE_PX = 512;
export const SURFACE_MIN_ZOOM = 12;
export const SURFACE_MAX_ZOOM = 24;
/** Une matière teintée de la couleur du fond n'apparaît qu'en se rapprochant : de loin, elle n'ajoute que du bruit. */
export const TINTED_SURFACE_ZOOMS = [19.5, 20.5] as const;

/** Part visible d'une matière teintée au zoom donné, de 0 à 1. */
export function tintedSurfaceShare(zoom: number): number {
  const [from, to] = TINTED_SURFACE_ZOOMS;
  return Math.min(1, Math.max(0, (zoom - from) / (to - from)));
}
const EARTH_CIRCUMFERENCE_M = 40075016.686;
const IMAGE_PREFIX = "surface:";
const VARIATION_PREFIX = "surface-variation@";
/**
 * Le voile double exactement de taille à chaque palier (1 px au zoom 12, 1024 px au zoom 22, ~25 m au sol) :
 * une taille arrondie au plus près décalerait tout le motif d'un palier à l'autre.
 */
const VARIATION_BASE_ZOOM = 12;
export const VARIATION_LAST_ZOOM = 22;
const VARIATION_CELLS = 7;
const VARIATION_LIGHT_ALPHA = 0.13;
const VARIATION_DARK_ALPHA = 0.04;

const SCALE_FACTOR: Record<SurfaceScale, number> = { fine: 0.6, normal: 1, large: 1.7 };

type Draw = (ctx: CanvasRenderingContext2D, size: number, rand: () => number) => void;

interface SurfaceDefinition {
  meters: number;
  draw: Draw;
}

function seeded(seed: number): () => number {
  let state = seed >>> 0 || 1;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return ((state >>> 0) % 100000) / 100000;
  };
}

function wrapped(size: number, x: number, y: number, draw: (x: number, y: number) => void): void {
  for (const dx of [-size, 0, size]) for (const dy of [-size, 0, size]) draw(x + dx, y + dy);
}

function blob(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, sides: number, rand: () => number): void {
  ctx.beginPath();
  for (let i = 0; i < sides; i++) {
    const angle = (i / sides) * Math.PI * 2;
    const r = radius * (0.75 + rand() * 0.45);
    const px = x + Math.cos(angle) * r;
    const py = y + Math.sin(angle) * r * 0.85;
    if (i) ctx.lineTo(px, py);
    else ctx.moveTo(px, py);
  }
  ctx.closePath();
}

function grey(value: number, warm = 0): string {
  const v = Math.round(value);
  return `rgb(${v},${v - warm},${v - warm * 2})`;
}

const SURFACES: Record<SurfaceMaterial, SurfaceDefinition> = {
  grass: {
    meters: 2,
    draw(ctx, n, rand) {
      ctx.fillStyle = "#7fae4f";
      ctx.fillRect(0, 0, n, n);
      for (let i = 0; i < 50; i++) {
        wrapped(n, rand() * n, rand() * n, (x, y) => {
          ctx.fillStyle = rand() > 0.5 ? "#85b553" : "#79a74a";
          blob(ctx, x, y, n * (0.035 + rand() * 0.045), 6, rand);
          ctx.fill();
        });
      }
      ctx.lineWidth = n / 128;
      ctx.lineCap = "round";
      for (let i = 0; i < 420; i++) {
        const x = rand() * n;
        const y = rand() * n;
        const length = n * (0.016 + rand() * 0.024);
        ctx.strokeStyle = rand() > 0.5 ? "#5d8c3a" : "#9cc867";
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + (rand() - 0.5) * n * 0.012, y - length);
        ctx.stroke();
      }
    },
  },
  dirt: {
    meters: 4.5,
    draw(ctx, n, rand) {
      ctx.fillStyle = "#9b7650";
      ctx.fillRect(0, 0, n, n);
      for (let i = 0; i < 70; i++) {
        wrapped(n, rand() * n, rand() * n, (x, y) => {
          ctx.fillStyle = rand() > 0.5 ? "#957049" : "#a07b54";
          blob(ctx, x, y, n * (0.025 + rand() * 0.05), 7, rand);
          ctx.fill();
        });
      }
      for (let i = 0; i < 520; i++) {
        ctx.fillStyle = rand() > 0.5 ? "#7a5b3b" : "#b89270";
        ctx.fillRect(rand() * n, rand() * n, n / 128, n / 128);
      }
      ctx.strokeStyle = "rgba(70,48,28,.2)";
      ctx.lineWidth = n / 400;
      ctx.lineCap = "round";
      for (let crack = 0; crack < 16; crack++) {
        let [x, y] = [rand() * n, rand() * n];
        let angle = rand() * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(x, y);
        for (let step = 0; step < 5; step++) {
          angle += (rand() - 0.5) * 1.2;
          x += Math.cos(angle) * n * 0.035;
          y += Math.sin(angle) * n * 0.035;
          ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      for (let i = 0; i < 90; i++) {
        wrapped(n, rand() * n, rand() * n, (x, y) => {
          const radius = n * (0.004 + rand() * 0.006);
          ctx.fillStyle = rand() > 0.5 ? "#8a7258" : "#a48b6e";
          blob(ctx, x, y, radius, 5, rand);
          ctx.fill();
          ctx.fillStyle = "rgba(255,240,215,.25)";
          blob(ctx, x - radius * 0.3, y - radius * 0.35, radius * 0.45, 4, rand);
          ctx.fill();
        });
      }
    },
  },
  sand: {
    meters: 3,
    draw(ctx, n, rand) {
      ctx.fillStyle = "#e3c88e";
      ctx.fillRect(0, 0, n, n);
      const rows = 7;
      for (let row = 0; row < rows; row++) {
        const ridge = (x: number) => row * (n / rows) + Math.sin((x / n) * Math.PI * 4 + row * 1.7) * n * 0.022 + Math.sin((x / n) * Math.PI * 2 + row) * n * 0.012;
        for (const [offset, color, width] of [[n * 0.012, "rgba(176,140,82,.45)", n / 90], [-n * 0.006, "rgba(255,244,214,.6)", n / 150]] as const) {
          ctx.strokeStyle = color;
          ctx.lineWidth = width;
          ctx.beginPath();
          for (let x = 0; x <= n; x += n / 48) {
            if (x) ctx.lineTo(x, ridge(x) + offset);
            else ctx.moveTo(x, ridge(x) + offset);
          }
          ctx.stroke();
        }
      }
      for (let i = 0; i < 1400; i++) {
        ctx.fillStyle = rand() > 0.5 ? "#c9ab6f" : "#f0dcae";
        ctx.fillRect(rand() * n, rand() * n, n / 170, n / 170);
      }
    },
  },
  gravel: {
    meters: 2.5,
    draw(ctx, n, rand) {
      ctx.fillStyle = "#a29e95";
      ctx.fillRect(0, 0, n, n);
      for (let i = 0; i < 1500; i++) {
        wrapped(n, rand() * n, rand() * n, (x, y) => {
          const radius = n * (0.004 + rand() * rand() * 0.016);
          ctx.fillStyle = "rgba(60,56,50,.28)";
          blob(ctx, x + radius * 0.25, y + radius * 0.35, radius, 5, rand);
          ctx.fill();
          ctx.fillStyle = grey(135 + rand() * 80, rand() > 0.5 ? 5 : -2);
          blob(ctx, x, y, radius, 5, rand);
          ctx.fill();
          ctx.fillStyle = "rgba(255,255,255,.18)";
          blob(ctx, x - radius * 0.3, y - radius * 0.3, radius * 0.4, 4, rand);
          ctx.fill();
        });
      }
    },
  },
  cobbles: {
    meters: 2,
    draw(ctx, n, rand) {
      ctx.fillStyle = "#6f6a63";
      ctx.fillRect(0, 0, n, n);
      const count = 8;
      const cell = n / count;
      for (let row = 0; row < count; row++) {
        for (let column = 0; column <= count; column++) {
          const x = column * cell + (row % 2 ? cell / 2 : 0) - cell / 2;
          const y = row * cell;
          ctx.fillStyle = grey(150 + rand() * 40, 6);
          ctx.beginPath();
          ctx.roundRect(x + cell * 0.03, y + cell * 0.03, cell * 0.94, cell * 0.94, cell * 0.1);
          ctx.fill();
          ctx.fillStyle = "rgba(255,255,255,.12)";
          ctx.fillRect(x + cell * 0.08, y + cell * 0.06, cell * 0.8, cell * 0.05);
        }
      }
    },
  },
  slabs: {
    meters: 4,
    draw(ctx, n, rand) {
      ctx.fillStyle = "#b9b4aa";
      ctx.fillRect(0, 0, n, n);
      const count = 4;
      const cell = n / count;
      const joint = n / 170;
      for (let row = 0; row < count; row++) {
        for (let column = 0; column < count; column++) {
          ctx.fillStyle = grey(196 + rand() * 22, 4);
          ctx.fillRect(column * cell + joint, row * cell + joint, cell - joint * 2, cell - joint * 2);
        }
      }
      for (let i = 0; i < 600; i++) {
        ctx.fillStyle = "rgba(0,0,0,.06)";
        ctx.fillRect(rand() * n, rand() * n, n / 128, n / 128);
      }
    },
  },
  deck: {
    meters: 2.4,
    draw(ctx, n, rand) {
      const planks = 12;
      const width = n / planks;
      const joists = 8;
      const step = n / joists;
      const gap = n / 300;
      ctx.fillStyle = "#3e2a1a";
      ctx.fillRect(0, 0, n, n);
      let previous = new Set<number>();
      for (let row = 0; row < planks; row++) {
        const y = row * width;
        const joints: number[] = [];
        const count = 2 + Math.floor(rand() * 2);
        for (let attempt = 0; joints.length < count && attempt < 40; attempt++) {
          const joint = Math.floor(rand() * joists);
          if (previous.has(joint) || joints.some((other) => Math.min(Math.abs(other - joint), joists - Math.abs(other - joint)) < 2)) continue;
          joints.push(joint);
        }
        joints.sort((a, b) => a - b);
        previous = new Set(joints);
        const cuts = joints.map((joint) => joint * step + step / 2);
        for (let piece = 0; piece < cuts.length; piece++) {
          const from = cuts[piece]!;
          const to = piece + 1 < cuts.length ? cuts[piece + 1]! : cuts[0]! + n;
          const shade = (rand() - 0.5) * 14;
          const [r, g, b] = [160 + shade, 117 + shade * 0.9, 79 + shade * 0.7];
          const gradient = ctx.createLinearGradient(0, y, 0, y + width);
          gradient.addColorStop(0, `rgb(${r + 10},${g + 8},${b + 5})`);
          gradient.addColorStop(1, `rgb(${r - 9},${g - 8},${b - 6})`);
          for (const offset of [-n, 0, n]) {
            const left = from + offset + gap;
            const right = to + offset - gap;
            if (right < 0 || left > n) continue;
            ctx.fillStyle = gradient;
            ctx.fillRect(left, y + gap, right - left, width - gap * 2);
            ctx.fillStyle = "rgba(255,236,205,.18)";
            ctx.fillRect(left, y + gap, right - left, n / 240);
            ctx.strokeStyle = "rgba(78,50,28,.26)";
            ctx.lineWidth = n / 420;
            for (let line = 0; line < 4; line++) {
              const base = y + width * (0.2 + line * 0.2 + (rand() - 0.5) * 0.08);
              const wave = width * (0.04 + rand() * 0.05);
              ctx.beginPath();
              ctx.moveTo(left, base);
              ctx.bezierCurveTo(left + (right - left) * 0.33, base + wave, left + (right - left) * 0.66, base - wave, right, base);
              ctx.stroke();
            }
            if (rand() > 0.6) {
              const kx = left + (right - left) * (0.15 + rand() * 0.7);
              const ky = y + width * (0.3 + rand() * 0.4);
              ctx.strokeStyle = "rgba(70,44,24,.4)";
              ctx.lineWidth = n / 360;
              for (const ring of [1, 2]) {
                ctx.beginPath();
                ctx.ellipse(kx, ky, width * 0.09 * ring, width * 0.05 * ring, 0, 0, Math.PI * 2);
                ctx.stroke();
              }
            }
            for (let joist = 0; joist < joists; joist += 2) {
              const sx = joist * step + offset;
              if (sx < left + width * 0.2 || sx > right - width * 0.2) continue;
              ctx.fillStyle = "rgba(55,40,30,.5)";
              for (const sy of [y + width * 0.28, y + width * 0.72]) {
                ctx.beginPath();
                ctx.arc(sx, sy, n / 280, 0, Math.PI * 2);
                ctx.fill();
              }
            }
          }
        }
      }
    },
  },
  chips: {
    meters: 1.5,
    draw(ctx, n, rand) {
      ctx.fillStyle = "#5e432a";
      ctx.fillRect(0, 0, n, n);
      const tones: [number, number, number][] = [[176, 126, 78], [152, 106, 64], [196, 150, 100], [134, 92, 56], [208, 168, 118]];
      for (let i = 0; i < 520; i++) {
        wrapped(n, rand() * n, rand() * n, (x, y) => {
          const [r, g, b] = tones[Math.floor(rand() * tones.length)]!;
          const length = n * (0.022 + rand() * 0.034);
          const thickness = length * (0.22 + rand() * 0.2);
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate(rand() * Math.PI);
          ctx.fillStyle = "rgba(30,20,12,.35)";
          ctx.fillRect(-length / 2 + thickness * 0.3, -thickness / 2 + thickness * 0.35, length, thickness);
          ctx.fillStyle = `rgb(${r},${g},${b})`;
          ctx.fillRect(-length / 2, -thickness / 2, length, thickness);
          ctx.fillStyle = "rgba(255,240,215,.22)";
          ctx.fillRect(-length / 2, -thickness / 2, length, thickness * 0.3);
          ctx.restore();
        });
      }
    },
  },
  concrete: {
    meters: 4,
    draw(ctx, n, rand) {
      ctx.fillStyle = "#c7c6c1";
      ctx.fillRect(0, 0, n, n);
      for (let i = 0; i < 1800; i++) {
        ctx.fillStyle = rand() > 0.5 ? "rgba(0,0,0,.05)" : "rgba(255,255,255,.12)";
        ctx.fillRect(rand() * n, rand() * n, n / 128, n / 128);
      }
      for (let i = 0; i < 14; i++) {
        wrapped(n, rand() * n, rand() * n, (x, y) => {
          ctx.fillStyle = rand() > 0.5 ? "rgba(0,0,0,.035)" : "rgba(255,255,255,.06)";
          blob(ctx, x, y, n * (0.05 + rand() * 0.08), 7, rand);
          ctx.fill();
        });
      }
      ctx.strokeStyle = "rgba(255,255,255,.07)";
      ctx.lineWidth = n / 200;
      for (let i = 0; i < 18; i++) {
        const x = rand() * n;
        const y = rand() * n;
        const radius = n * (0.08 + rand() * 0.12);
        ctx.beginPath();
        ctx.arc(x, y, radius, rand() * Math.PI, rand() * Math.PI + 1.2);
        ctx.stroke();
      }
    },
  },
};

const HEX = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i;

/** La couleur sans sa transparence, en `rrggbb` ; `null` pour ce qui n'est pas une couleur lisible. */
export function opaqueHex(color: string | undefined): string | null {
  if (!color) return null;
  const hex = HEX.exec(color.trim())?.[1];
  if (hex) {
    const full = hex.length <= 4 ? [...hex.slice(0, 3)].map((c) => c + c).join("") : hex.slice(0, 6);
    return full.toLowerCase();
  }
  const rgb = RGB.exec(color.trim());
  if (!rgb) return null;
  return rgb.slice(1, 4).map((c) => Math.max(0, Math.min(255, Math.round(Number(c)))).toString(16).padStart(2, "0")).join("");
}

/** Ce qui distingue une image de sol d'une autre, palier de zoom mis à part. */
export function surfaceImageKey(surface: ShapeSurface, bgColor: string | undefined): string {
  const tint = surface.tinted ? opaqueHex(bgColor) ?? "" : "";
  return `${surface.material}:${surface.scale ?? "normal"}:${tint}`;
}

export function surfaceImageId(key: string, zoom: number): string {
  return `${IMAGE_PREFIX}${key}@${zoom}`;
}

export function parseSurfaceImageId(id: string): { material: SurfaceMaterial; scale: SurfaceScale; tint: string | null; zoom: number } | null {
  if (!id.startsWith(IMAGE_PREFIX)) return null;
  const match = /^([a-z]+):([a-z]+):([0-9a-f]{6})?@(\d+)$/.exec(id.slice(IMAGE_PREFIX.length));
  if (!match) return null;
  const [, material, scale, tint, zoom] = match;
  if (!isSurfaceMaterial(material) || !isSurfaceScale(scale)) return null;
  return { material, scale, tint: tint ?? null, zoom: Number(zoom) };
}

/** Une image par palier de zoom : un motif MapLibre garde sa taille en pixels, chaque palier la ramène à la même taille en mètres. */
export function surfacePatternExpression(): maplibregl.ExpressionSpecification {
  const imageAt = (zoom: number): maplibregl.ExpressionSpecification => ["concat", IMAGE_PREFIX, ["get", "surface"], `@${zoom}`];
  const stops: (number | maplibregl.ExpressionSpecification)[] = [];
  for (let zoom = SURFACE_MIN_ZOOM + 1; zoom <= SURFACE_MAX_ZOOM; zoom++) stops.push(zoom, imageAt(zoom));
  return ["step", ["zoom"], imageAt(SURFACE_MIN_ZOOM), ...stops] as maplibregl.ExpressionSpecification;
}

export function surfaceVariationImageId(zoom: number): string {
  return `${VARIATION_PREFIX}${zoom}`;
}

/** Le voile de taches à grande échelle posé sur les matières, une image par palier comme elles. */
export function surfaceVariationExpression(): maplibregl.ExpressionSpecification {
  const stops: (number | string)[] = [];
  for (let zoom = SURFACE_MIN_ZOOM + 1; zoom <= SURFACE_MAX_ZOOM; zoom++) stops.push(zoom, surfaceVariationImageId(zoom));
  return ["step", ["zoom"], surfaceVariationImageId(SURFACE_MIN_ZOOM), ...stops] as maplibregl.ExpressionSpecification;
}

/** Bruit lisse qui se raccorde sur ses bords, de 0 à 1. */
export function variationValue(u: number, v: number): number {
  const octave = (cells: number, seed: number) => {
    const at = (i: number, j: number) => {
      const x = ((i % cells) + cells) % cells;
      const y = ((j % cells) + cells) % cells;
      const h = Math.sin(x * 127.1 + y * 311.7 + seed * 74.7) * 43758.5453;
      return h - Math.floor(h);
    };
    const [x, y] = [u * cells, v * cells];
    const [i, j] = [Math.floor(x), Math.floor(y)];
    const [fx, fy] = [x - i, y - j];
    const [sx, sy] = [fx * fx * (3 - 2 * fx), fy * fy * (3 - 2 * fy)];
    const top = at(i, j) + (at(i + 1, j) - at(i, j)) * sx;
    const bottom = at(i, j + 1) + (at(i + 1, j + 1) - at(i, j + 1)) * sx;
    return top + (bottom - top) * sy;
  };
  return octave(VARIATION_CELLS, 1) * 0.65 + octave(VARIATION_CELLS * 3, 2) * 0.35;
}

let variationCanvas: HTMLCanvasElement | null = null;

function variationSource(): HTMLCanvasElement {
  if (variationCanvas) return variationCanvas;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = SOURCE_PX;
  const ctx = canvas.getContext("2d")!;
  const image = ctx.createImageData(SOURCE_PX, SOURCE_PX);
  for (let y = 0; y < SOURCE_PX; y++) {
    for (let x = 0; x < SOURCE_PX; x++) {
      const value = variationValue(x / SOURCE_PX, y / SOURCE_PX) * 2 - 1;
      const index = (y * SOURCE_PX + x) * 4;
      const light = value > 0;
      image.data[index] = light ? 255 : 90;
      image.data[index + 1] = light ? 252 : 70;
      image.data[index + 2] = light ? 240 : 50;
      image.data[index + 3] = Math.round(Math.min(1, Math.abs(value) * 1.6) * (light ? VARIATION_LIGHT_ALPHA : VARIATION_DARK_ALPHA) * 255);
    }
  }
  ctx.putImageData(image, 0, 0);
  variationCanvas = canvas;
  return canvas;
}

export function metersPerPixelAt(latitude: number, zoom: number): number {
  return (EARTH_CIRCUMFERENCE_M * Math.cos((latitude * Math.PI) / 180)) / (512 * 2 ** zoom);
}

export function surfaceTileMeters(material: SurfaceMaterial, scale: SurfaceScale): number {
  return SURFACES[material].meters * SCALE_FACTOR[scale];
}

const TINT_CONTRAST = 0.85;

/**
 * La matière prend la couleur de fond en moyenne : chaque pixel garde seulement son écart de clarté
 * au reste du motif. Une terre sombre teintée d'un orange pâle reste pâle.
 */
export function tintPixels(data: Uint8ClampedArray, tint: string): void {
  const value = parseInt(tint, 16);
  const color = [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  const luminance = (index: number) => 0.2126 * data[index]! + 0.7152 * data[index + 1]! + 0.0722 * data[index + 2]!;
  let total = 0;
  for (let index = 0; index < data.length; index += 4) total += luminance(index);
  const mean = total / (data.length / 4) || 1;
  for (let index = 0; index < data.length; index += 4) {
    const factor = 1 + (luminance(index) / mean - 1) * TINT_CONTRAST;
    for (let channel = 0; channel < 3; channel++) data[index + channel] = color[channel]! * factor;
  }
}

const sources = new Map<string, HTMLCanvasElement>();

export function sourceCanvas(material: SurfaceMaterial, tint: string | null): HTMLCanvasElement {
  const key = `${material}:${tint ?? ""}`;
  const cached = sources.get(key);
  if (cached) return cached;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = SOURCE_PX;
  const ctx = canvas.getContext("2d")!;
  SURFACES[material].draw(ctx, SOURCE_PX, seeded(material.length * 7919 + material.charCodeAt(0)));
  if (tint) {
    const image = ctx.getImageData(0, 0, SOURCE_PX, SOURCE_PX);
    tintPixels(image.data, tint);
    ctx.putImageData(image, 0, 0);
  }
  sources.set(key, canvas);
  return canvas;
}

function resized(source: HTMLCanvasElement, size: number): HTMLCanvasElement {
  let current = source;
  while (current.width / 2 >= size) {
    const half = document.createElement("canvas");
    half.width = half.height = current.width / 2;
    const ctx = half.getContext("2d")!;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(current, 0, 0, half.width, half.height);
    current = half;
  }
  const target = document.createElement("canvas");
  target.width = target.height = size;
  const ctx = target.getContext("2d")!;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(current, 0, 0, size, size);
  return target;
}

/** Des pixels RVBA, tels que `map.addImage` les accepte. */
export interface Pixels {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

/** Moyenne de quatre pixels en deux, pondérée par leur opacité comme le fait le navigateur. */
export function halved({ width, height, data }: Pixels): Pixels {
  const w = Math.max(1, width >> 1);
  const h = Math.max(1, height >> 1);
  const out = new Uint8ClampedArray(w * h * 4);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      for (let dy = 0; dy < 2; dy++) {
        for (let dx = 0; dx < 2; dx++) {
          const at = (Math.min(height - 1, y * 2 + dy) * width + Math.min(width - 1, x * 2 + dx)) * 4;
          const alpha = data[at + 3]!;
          r += data[at]! * alpha;
          g += data[at + 1]! * alpha;
          b += data[at + 2]! * alpha;
          a += alpha;
        }
      }
      const index = (y * w + x) * 4;
      if (a) {
        out[index] = r / a;
        out[index + 1] = g / a;
        out[index + 2] = b / a;
      }
      out[index + 3] = a / 4;
    }
  }
  return { width: w, height: h, data: out };
}

/** Le carreau ramené à `size` pixels de côté par interpolation, en bouclant sur ses bords : le motif reste raccordable. */
export function resampled({ width, height, data }: Pixels, size: number): Pixels {
  const out = new Uint8ClampedArray(size * size * 4);
  for (let y = 0; y < size; y++) {
    const sy = ((y + 0.5) * height) / size - 0.5;
    const y0 = Math.floor(sy);
    const fy = sy - y0;
    const rows = [((y0 % height) + height) % height, (((y0 + 1) % height) + height) % height];
    for (let x = 0; x < size; x++) {
      const sx = ((x + 0.5) * width) / size - 0.5;
      const x0 = Math.floor(sx);
      const fx = sx - x0;
      const columns = [((x0 % width) + width) % width, (((x0 + 1) % width) + width) % width];
      const index = (y * size + x) * 4;
      for (let channel = 0; channel < 4; channel++) {
        const at = (row: number, column: number) => data[(rows[row]! * width + columns[column]!) * 4 + channel]!;
        out[index + channel] = (at(0, 0) * (1 - fx) + at(0, 1) * fx) * (1 - fy) + (at(1, 0) * (1 - fx) + at(1, 1) * fx) * fy;
      }
    }
  }
  return { width: size, height: size, data: out };
}

const mipChains = new WeakMap<HTMLCanvasElement, Pixels[]>();

/**
 * Le carreau source à `size` pixels, sans toile : relire une toile du navigateur passe par le GPU et
 * coûtait plusieurs millisecondes par image, même minuscule. Les pixels de la source sont lus une
 * fois, puis réduits de moitié en moitié, une fois pour toutes.
 */
function resizedPixels(source: HTMLCanvasElement, size: number): Pixels {
  let chain = mipChains.get(source);
  if (!chain) {
    const image = source.getContext("2d")!.getImageData(0, 0, source.width, source.height);
    chain = [{ width: image.width, height: image.height, data: image.data }];
    while (chain.at(-1)!.width > 1) chain.push(halved(chain.at(-1)!));
    mipChains.set(source, chain);
  }
  const nearest = [...chain].reverse().find((level) => level.width >= size) ?? chain[0]!;
  return nearest.width === size ? { ...nearest, data: nearest.data.slice() } : resampled(nearest, size);
}

const EXACT_SIZE_FROM_ZOOM = 18;

/**
 * Taille en pixels d'un carreau. À partir de `EXACT_SIZE_FROM_ZOOM`, elle double exactement d'un palier
 * à l'autre : arrondie au plus près à chaque palier, elle décalerait tout le motif en changeant de zoom.
 */
export function surfaceImageSize(meters: number, latitude: number, zoom: number): number {
  if (zoom < EXACT_SIZE_FROM_ZOOM) return Math.max(1, Math.round(meters / metersPerPixelAt(latitude, zoom)));
  return Math.max(1, Math.round(meters / metersPerPixelAt(latitude, EXACT_SIZE_FROM_ZOOM))) * 2 ** (zoom - EXACT_SIZE_FROM_ZOOM);
}

/** Ajoute l'image de sol que MapLibre réclame (`styleimagemissing`) ; `false` si l'identifiant n'en est pas une. */
export function addSurfaceImage(map: maplibregl.Map, id: string, latitude: number): boolean {
  if (id.startsWith(VARIATION_PREFIX)) {
    const zoom = Number(id.slice(VARIATION_PREFIX.length));
    if (!Number.isFinite(zoom)) return false;
    const size = 2 ** (Math.min(zoom, VARIATION_LAST_ZOOM) - VARIATION_BASE_ZOOM);
    map.addImage(id, resizedPixels(variationSource(), size));
    return true;
  }
  const parsed = parseSurfaceImageId(id);
  if (!parsed) return false;
  // Un motif se dessine à la taille de son image en pixels CSS, quel que soit son `pixelRatio`.
  const size = surfaceImageSize(surfaceTileMeters(parsed.material, parsed.scale), latitude, parsed.zoom);
  map.addImage(id, resizedPixels(sourceCanvas(parsed.material, parsed.tint), size));
  return true;
}

/** Vignette d'une matière, pour un sélecteur. */
export function surfacePreviewUrl(material: SurfaceMaterial, size = 64, tint?: string): string {
  return resized(sourceCanvas(material, opaqueHex(tint)), size).toDataURL();
}

const BORDER_PREFIX = "surface-border:";
const BORDER_IMAGE_HEIGHT = 64;

interface BorderDefinition {
  meters: number;
  /** Longueur du motif, en largeurs de bordure. */
  ratio: number;
  draw?: (ctx: CanvasRenderingContext2D, width: number, height: number, rand: () => number) => void;
}

const BORDERS: Record<SurfaceBorder, BorderDefinition> = {
  stone: {
    meters: 0.4,
    ratio: 4,
    draw(ctx, w, h, rand) {
      ctx.fillStyle = "#77746d";
      ctx.fillRect(0, 0, w, h);
      const blocks = 3;
      const length = w / blocks;
      const joint = h * 0.06;
      for (let i = 0; i < blocks; i++) {
        ctx.fillStyle = grey(168 + rand() * 30, 3);
        ctx.fillRect(i * length + joint, joint, length - joint * 2, h - joint * 2);
        ctx.fillStyle = "rgba(255,255,255,.18)";
        ctx.fillRect(i * length + joint, joint, length - joint * 2, h * 0.14);
      }
    },
  },
  wood: {
    meters: 0.3,
    ratio: 8,
    draw(ctx, w, h, rand) {
      ctx.fillStyle = "#8a5d36";
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = "#6b4426";
      ctx.fillRect(0, 0, w, h * 0.08);
      ctx.fillRect(0, h * 0.92, w, h * 0.08);
      const planks = 2;
      for (let i = 0; i < planks; i++) {
        const x = (i * w) / planks;
        ctx.fillStyle = "#5c3a20";
        ctx.fillRect(x, 0, h * 0.06, h);
        ctx.strokeStyle = "rgba(60,36,18,.3)";
        ctx.lineWidth = h * 0.03;
        for (let grain = 0; grain < 3; grain++) {
          const y = h * (0.25 + grain * 0.25 + (rand() - 0.5) * 0.08);
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.bezierCurveTo(x + w / planks / 3, y + h * 0.05, x + (2 * w) / planks / 3, y - h * 0.05, x + w / planks, y);
          ctx.stroke();
        }
      }
    },
  },
  pebbles: {
    meters: 0.8,
    ratio: 4,
    draw(ctx, w, h, rand) {
      ctx.fillStyle = "#5f5a52";
      ctx.fillRect(0, 0, w, h);
      for (let i = 0; i < 260; i++) {
        const x = rand() * w;
        const y = rand() * h;
        const radius = h * (0.03 + rand() * 0.05);
        const tone = 92 + rand() * 40;
        for (const dx of [-w, 0, w]) {
          ctx.fillStyle = `rgb(${tone},${tone - 4},${tone - 10})`;
          ctx.beginPath();
          ctx.ellipse(x + dx, y, radius, radius * 0.75, rand() * Math.PI, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    },
  },
  fade: {
    meters: 1.2,
    ratio: 1,
  },
};

export function surfaceBorderMeters(border: SurfaceBorder): number {
  return BORDERS[border].meters;
}

/** Le fondu reprend la matière de la forme, ou sa couleur de fond quand elle n'en a pas. */
export function surfaceBorderImageKey(border: SurfaceBorder, surface: ShapeSurface | undefined, bgColor: string | undefined): string | null {
  if (border !== "fade") return border;
  if (surface) return `fade:${surfaceImageKey(surface, bgColor)}`;
  const color = opaqueHex(bgColor);
  return color ? `fade:solid:${color}` : null;
}

export function surfaceBorderImageId(key: string): string {
  return `${BORDER_PREFIX}${key}`;
}

export function surfaceBorderPatternExpression(): maplibregl.ExpressionSpecification {
  return ["concat", BORDER_PREFIX, ["get", "surfaceBorder"]];
}

function borderCanvas(key: string): HTMLCanvasElement | null {
  const h = BORDER_IMAGE_HEIGHT;
  if (key.startsWith("fade:")) {
    const rest = key.slice("fade:".length);
    const solid = /^solid:([0-9a-f]{6})$/.exec(rest);
    if (solid) {
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = h;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = `#${solid[1]}`;
      ctx.fillRect(0, 0, h, h);
      return canvas;
    }
    const parsed = parseSurfaceImageId(`${IMAGE_PREFIX}${rest}@0`);
    return parsed ? resized(sourceCanvas(parsed.material, parsed.tint), h * 2) : null;
  }
  const definition = BORDERS[key as SurfaceBorder];
  if (!definition?.draw) return null;
  const canvas = document.createElement("canvas");
  canvas.width = h * definition.ratio;
  canvas.height = h;
  definition.draw(canvas.getContext("2d")!, canvas.width, h, seeded(key.length * 104729 + key.charCodeAt(0)));
  return canvas;
}

/** Ajoute l'image de bordure que MapLibre réclame ; `false` si l'identifiant n'en est pas une. */
export function addSurfaceBorderImage(map: maplibregl.Map, id: string): boolean {
  if (!id.startsWith(BORDER_PREFIX)) return false;
  const canvas = borderCanvas(id.slice(BORDER_PREFIX.length));
  if (!canvas) return false;
  map.addImage(id, canvas.getContext("2d")!.getImageData(0, 0, canvas.width, canvas.height));
  return true;
}

/** Bande d'aperçu d'une bordure, pour un sélecteur. */
export function surfaceBorderPreviewUrl(border: SurfaceBorder, surface?: ShapeSurface, bgColor?: string): string | null {
  const key = surfaceBorderImageKey(border, surface, bgColor ?? "#c8b48a");
  const canvas = key ? borderCanvas(key) : null;
  if (!canvas) return null;
  if (border !== "fade") return canvas.toDataURL();
  const strip = document.createElement("canvas");
  strip.width = BORDER_IMAGE_HEIGHT * 4;
  strip.height = BORDER_IMAGE_HEIGHT;
  const ctx = strip.getContext("2d")!;
  for (let x = 0; x < strip.width; x += canvas.width) ctx.drawImage(canvas, x, 0, canvas.width, BORDER_IMAGE_HEIGHT);
  const fade = ctx.createLinearGradient(0, 0, 0, strip.height);
  fade.addColorStop(0, "rgba(0,0,0,1)");
  fade.addColorStop(1, "rgba(0,0,0,0)");
  ctx.globalCompositeOperation = "destination-in";
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, strip.width, strip.height);
  return strip.toDataURL();
}

/** Aperçu d'une finition de barrière vue de face, pour un sélecteur. */
export function railingPreviewUrl(style: RailingStyle, width = 96, height = 48): string {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  const top = style === "glass-high" ? height * 0.12 : height * 0.3;
  const base = height * 0.88;
  const posts = 3;
  const post = (x: number, color: string, thickness: number) => {
    ctx.fillStyle = color;
    ctx.fillRect(x - thickness / 2, top, thickness, base - top);
  };
  if (style === "glass" || style === "glass-high") {
    ctx.fillStyle = "rgba(150,200,220,.45)";
    ctx.fillRect(width * 0.05, top, width * 0.9, base - top);
    ctx.fillStyle = "rgba(255,255,255,.5)";
    ctx.fillRect(width * 0.12, top + 4, width * 0.05, base - top - 8);
    ctx.fillStyle = "#7d868c";
    ctx.fillRect(width * 0.03, top - 2, width * 0.94, 4);
    for (let i = 0; i < posts; i++) post(width * (0.05 + (i * 0.9) / (posts - 1)), "#7d868c", 3);
  } else if (style === "mesh") {
    ctx.strokeStyle = "rgba(90,98,104,.7)";
    ctx.lineWidth = 1;
    for (let x = -height; x < width + height; x += 6) {
      ctx.beginPath();
      ctx.moveTo(x, top);
      ctx.lineTo(x + (base - top), base);
      ctx.moveTo(x + (base - top), top);
      ctx.lineTo(x, base);
      ctx.stroke();
    }
    for (let i = 0; i < posts; i++) post(width * (0.05 + (i * 0.9) / (posts - 1)), "#5f676c", 3);
  } else if (style === "bars") {
    ctx.fillStyle = "#4f575c";
    ctx.fillRect(width * 0.03, top, width * 0.94, 3);
    ctx.fillRect(width * 0.03, base - 3, width * 0.94, 3);
    for (let x = width * 0.06; x < width * 0.95; x += 6) post(x, "#4f575c", 2);
  } else if (style === "hedge" || style === "hedge-high") {
    const greens = ["#527f33", "#5f8e3c", "#6e9d45", "#7aab4c", "#47742d"];
    const radius = style === "hedge" ? height * 0.2 : height * 0.19;
    const rows = style === "hedge" ? 2 : 3;
    const rand = seeded(style.length * 31);
    for (let row = 0; row < rows; row++) {
      const y = base - radius - row * radius * 1.15;
      for (let x = radius * 0.6; x < width - radius * 0.4; x += radius * 0.9) {
        const r = radius * (0.85 + rand() * 0.3);
        ctx.fillStyle = greens[Math.floor(rand() * greens.length)]!;
        ctx.beginPath();
        ctx.arc(x + (rand() - 0.5) * radius * 0.3, y + (rand() - 0.5) * radius * 0.3, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "rgba(255,255,230,.14)";
        ctx.beginPath();
        ctx.arc(x - r * 0.25, y - r * 0.3, r * 0.45, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  } else {
    const wood = style === "wood-white" ? ["#e9e4da", "#cfc8bb"] : ["#9b6c43", "#7a5130"];
    for (const y of [top + (base - top) * 0.15, top + (base - top) * 0.55]) {
      ctx.fillStyle = wood[0]!;
      ctx.fillRect(width * 0.03, y, width * 0.94, (base - top) * 0.2);
    }
    for (let i = 0; i < posts; i++) post(width * (0.05 + (i * 0.9) / (posts - 1)), wood[1]!, 5);
  }
  return canvas.toDataURL();
}
